<template>
    <header
        class="o-m-header m-header"
        :class="[{'m-header--fixed': currentUnit === 'point'}, {'m-header--landscape': isLandscape}]"
    >
        <component
            :is="currentUnit"
            :mode="mode"
            :start-point="startPoint"
            :finish-point="finishPoint"
            @onFocusInput="handleComponent('point')"
            @onClickBack="handleComponent('guide')"
            @onTypePoint="handleTypePoint"
            @onBack="handleBack"
            @startGuideView="handleStartGuideView"
            @onMenuClick="handleComponent('guide')"
            @onClose="handleClose"
            @returnToStart="handleReturnToStart"
            @changeExtremePoint="changeExtremePoint"
        />
        <!--
            Locked behaviours

        -->
        <div
            v-if="isShowBlueLine"
            class="m-header__line"
        />
    </header>
</template>

<script>
import {
    mapState,
    mapGetters,
    mapMutations
} from 'vuex';

import OMHeaderMain from '../../unique-organisms/o-m-header-main/o-m-header-main';
import OMHeaderGuide from '../../unique-organisms/o-m-header-guide/o-m-header-guide';
import OMChoosePoint from '../../unique-organisms/o-m-choose-point/o-m-choose-point';
import OMHeaderStartGuide from '../../unique-organisms/o-m-header-start-guide/o-m-header-start-guide';

import { MAP_SHOW_PATH } from "@/shared/components/map-viewer/constants/map-commands";
import {
    UI_MAP_READY,
    UI_STEP_SELECTED,
    UI_STEP_BY_STEP_DIRECTIONS
} from "@/shared/components/map-viewer/constants/map-events";
import {
    REBUILD_PATH,
    PATH_REBUILDED,
    GUIDE_NEW_POINT
} from '@/utils/constants/bus-events-constants';
import {
    MOBILE_CARE,
    MOBILE_ROOT,
    MOBILE_GUIDE,
    MOBILE_SERVICES
} from '@/utils/constants/pages';

export default {
    name: 'OMHeader',
    components: {
        'guide': OMHeaderGuide,
        'point': OMChoosePoint,
        'primary': OMHeaderMain,
        'startGuide': OMHeaderStartGuide
    },
    data() {
        return {
            to: this.$route.query.to || '',
            from: this.$route.query.from || '',
            currentUnit: null,
            mode: null,
            isGuideShow: false
        }
    },
    watch: {
        '$route'() {
            this.checkRoute();
            this.to = this.$route.query.to || '';
            this.from = this.$route.query.from || '';
        }
    },
    computed: {
        ...mapState({
            activePath: state => state.activePath,
            isLandscape: state => state.landscape,
            activeStepIndex: state => state.activeStepIndex
        }),
        ...mapGetters([
            't',
            'getObject',
            'getListItem',
            'isAppLoaded',
            'getMapLoading',
            'getMapConfigProp',
            'getDenormalisedObjectById',
            'getDenormalisedObjectsById',
        ]),
        isShowBlueLine() {
            return ([MOBILE_ROOT, MOBILE_CARE, MOBILE_SERVICES].includes(this.$route.name) && !this.isLandscape);
        },
        startPoint: {
            get() {
                const obj = this.getDenormalisedObjectById(this.t(this.finishPoint, 'closest_entrance'));

                let fromObject;
                if(this.$route.query.from)
                    fromObject = this.getDenormalisedObjectById(parseInt(this.from))
                else{
                    // eslint-disable-next-line camelcase
                    const closestEntranceId = this.finishPoint?.meta?.closest_entrance || this.getMapConfigProp('guide.default_entrance');
                    if(!closestEntranceId)
                        throw new Error('Closest entrance not set. Please set default entrance in the map config "guide.default_entrance"')

                    fromObject = this.getDenormalisedObjectsById(closestEntranceId).find( o => o.floor.meta.is_outdoor )
                }

                return {
                    name: this.t(fromObject, 'name'),
                    id: this.$route.query.from_unit || fromObject?.unit.name,
                    object_id: fromObject?.id
                }
            },
            set(data) {
                this.from = data;
            }
        },
        startPointObject(){
            return this.startPoint && this.getDenormalisedObjectById(this.startPoint.object_id || this.startPoint.id)
        },
        finishPoint: {
            get() {
                return this.to ? this.getDenormalisedObjectById(parseInt(this.to, 10)) : {};
            },
            set(data) {
                this.to = data;
            }
        }
    },
    mounted() {
        this.checkRoute();
        this.initEventsListeners();
        this.isAppLoaded && !this.getMapLoading && this.buildPath();
    },
    beforeDestroy() {
        this.deactivateEventsListeners();
    },
    methods: {
        ...mapMutations([
            'setMobileGuideStartActivate'
        ]),
        initEventsListeners() {
            this.$bus.$on(UI_MAP_READY, this.buildPath);
            this.$bus.$on(REBUILD_PATH, this.buildPath);
            this.$bus.$on(GUIDE_NEW_POINT, this.setGuidePoint);
            this.$bus.$on(UI_STEP_SELECTED, this.setActiveStep);
            this.$bus.$on(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
        },
        deactivateEventsListeners() {
            this.$bus.$off(UI_MAP_READY, this.buildPath);
            this.$bus.$off(REBUILD_PATH, this.buildPath);
            this.$bus.$off(GUIDE_NEW_POINT, this.setGuidePoint);
            this.$bus.$off(UI_STEP_SELECTED, this.setActiveStep);
            this.$bus.$off(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
        },
        handleComponent(unit) {
            this.currentUnit = unit;
            // if (unit === 'guide') this.buildPath();
        },
        handleTypePoint(data) {
            this.mode = data;
        },
        setActivePath(data) {
            this.$store.commit('setActivePathToState', data);
        },
        setActiveStep(index) {
            this.$store.commit('setActiveStepIndex', index);
            this.$store.commit('setActiveStep', this.activePath[index]);
        },
        checkRoute() {
            const isTwoPointsAvailable = this.$route.query.from && this.$route.query.to || false;
            const isQrParams = this.$route.params.qr || false;
            const isStartPoint = this.$route.query.from || '';
            const isClosest = this.$route.params.closest || false;

            if(this.isGuideShow) {
                this.currentUnit = 'guide';
                this.isGuideShow = false;
                return
            }

            if (isQrParams && isTwoPointsAvailable) {
                this.currentUnit = 'startGuide';
            } else if (isQrParams) {
                this.currentUnit = isClosest ? 'startGuide' : 'point';
                this.mode = isClosest ? null : isStartPoint ? 'finish' : 'start';
            } else  {
                this.currentUnit = this.$route.name === MOBILE_GUIDE ? 'startGuide' : 'primary';
            }
        },
        setGuidePoint(data) {
            const params = {
                query: {}
            };

            if (this.finishPoint.id === parseInt(data, 10)) {
                this.currentUnit = 'guide';
                return;
            }
            if (this.mode === 'start') {
                if (parseInt(this.$route.query.from, 10) === data) {
                    this.currentUnit = 'guide';
                    return;
                }
                this.startPoint = data;
                params.query.from = data;
                params.query.to = this.to;
            } else {
                if (parseInt(this.$route.query.to, 10) === data) {
                    this.currentUnit = 'guide';
                    return;
                }
                if (this.$route.query.from) {
                    params.query.from = this.from;
                    params.query.to = data;
                } else {
                    params.query.to = data;
                }
                this.finishPoint = data;
            }
            this.$router.replace(params);
            this.$bus.$emit(PATH_REBUILDED, true);
        },
        buildPath() {
            if (
                this.$route.name !== MOBILE_GUIDE ||
                (this.$route.query.qr && this.$route.query.to) ||
                !this.startPoint
            ) return;

            if(!_.has(this.finishPoint, 'id')) return;
            const params = {
                from: {},
                to: {
                    id: this.finishPoint.id
                },
                mode: this.$route.params.type, // this.getMapConfigProp('guide.mode', 'easy'),
            };
            if(_.has(this.startPointObject, 'meta.settings.guide_orientation') )
                params.initial_orientation = this.startPointObject.meta.settings.guide_orientation;
            params.from = this.$route.query.from_unit
                ? {
                    id: this.startPoint.object_id,
                    unit: this.startPoint.id,
                }
                : this.$route.query.from && this.startPointObject
                    ? {
                        id: this.startPointObject.id,
                        unit: this.startPointObject.unit.name
                    }
                    : {
                        id: this.startPoint.object_id,
                        unit: this.startPoint.id
                    };

            // eslint-disable-next-line camelcase
            if(this.finishPoint?.meta?.prioritise_outdoor)
                params.use_outside = 'prefer'

            if(!this.$route.params.qr && !this.$route.query.from )
                params.show_first_step_portal_message = true

            params.portal_rules = this.getMapConfigProp('guide.portal_rules', {});

            setTimeout( () => this.$bus.$emit(MAP_SHOW_PATH, params), 500);

            //this.$bus.$emit(MAP_SHOW_PATH, params);
        },
        handleBack() {
            const params = this.$route.params.type
                ? { name: `mobile.${this.$route.params.type}`}
                : { name: MOBILE_ROOT };
            if (this.$route.params.type === 'service') params.name = MOBILE_SERVICES;
            //TODO: need to adjust consistency name route between themes
            if (this.$route.params.type === 'start') params.name = MOBILE_ROOT;
            this.$router.push(params);
        },
        handleClose() {
            this.$router.push({ name: MOBILE_ROOT });
        },
        handleReturnToStart() {
            this.currentUnit = 'startGuide';
        },
        handleStartGuideView() {
            this.setMobileGuideStartActivate(true)
            this.currentUnit = 'startGuide';
        },
        /**
         * If we change extreme point in: OMCare or OMServise,
         * we need to show OMHeaderGuide
         */
        changeExtremePoint() {
            this.isGuideShow = true
        },
    }
}
</script>

<style lang="scss">
@import "o-m-header";
</style>
